import React from 'react';
import { Button } from '@fluentui/react-components';
import { ArrowLeft24Regular, ChevronDown16Regular, ChevronUp16Regular, Document16Regular } from '@fluentui/react-icons';
import type { SuggestedPromptCategoryOption, SuggestedPromptOption } from '../../../hooks/useSuggestedPrompts';
import { useSuggestedPromptsMenuStyles } from './SuggestedPromptsMenu';

interface PromptSuggestionsViewProps {
    suggestedPromptCategories: SuggestedPromptCategoryOption[];
    expandedCategories: Set<string>;
    onBack: () => void;
    onToggleCategory: (categoryId: string) => void;
    onPromptSelect: (prompt: string) => void;
}

const PromptSuggestionsView: React.FC<PromptSuggestionsViewProps> = ({
    suggestedPromptCategories,
    expandedCategories,
    onBack,
    onToggleCategory,
    onPromptSelect,
}) => {
    const styles = useSuggestedPromptsMenuStyles();

    const renderPromptOption = React.useCallback(
        (prompt: SuggestedPromptOption, isNested = false): React.ReactNode => (
            <React.Fragment key={prompt.id}>
                <Button
                    appearance="transparent"
                    className={styles.promptButton}
                    style={isNested ? { marginLeft: '18px', width: 'calc(100% - 18px)' } : undefined}
                    onClick={() => onPromptSelect(prompt.text)}
                >
                    <span className={styles.promptText}>{prompt.title}</span>
                    {prompt.requiresDocument && <Document16Regular className={styles.documentIcon} />}
                </Button>
                {(prompt.children ?? []).map((childPrompt) => renderPromptOption(childPrompt, true))}
            </React.Fragment>
        ),
        [onPromptSelect, styles.documentIcon, styles.promptButton, styles.promptText],
    );

    return (
        <>
            <div className={styles.backButtonContainer}>
                <Button
                    appearance="transparent"
                    className={styles.backButton}
                    icon={<ArrowLeft24Regular />}
                    onClick={onBack}
                >
                    Back
                </Button>
            </div>
            <div className={styles.entriesScrollArea}>
                {suggestedPromptCategories.length === 0 ? (
                    <div className={styles.emptyText}>No prompt suggestions available.</div>
                ) : (
                    suggestedPromptCategories.map((category) => {
                        const isExpanded = expandedCategories.has(category.id);
                        return (
                            <div key={category.id} className={styles.section}>
                                <Button
                                    appearance="transparent"
                                    className={styles.sectionHeaderButton}
                                    onClick={() => onToggleCategory(category.id)}
                                >
                                    <span className={styles.sectionHeaderText}>{category.title}</span>
                                    <span className={styles.sectionHeaderRight}>
                                        {category.requiresDocument && (
                                            <Document16Regular className={styles.categoryDocIcon} />
                                        )}
                                        {isExpanded ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
                                    </span>
                                </Button>
                                {isExpanded && category.items.map((prompt) => renderPromptOption(prompt))}
                            </div>
                        );
                    })
                )}
            </div>
        </>
    );
};

export default PromptSuggestionsView;
